import { Drawer, Portal, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import { useBlockPaletteDrag } from './blockPaletteDragContext';
import {
  PALETTE_HEADER_OFFSET,
  PALETTE_PANEL_WIDTH,
  PALETTE_SHEET_HEIGHT,
} from './blockPaletteConstants';
import BlockPaletteEdgeTabs from './BlockPaletteEdgeTabs';
import { useEditorSidebar } from './editorSidebarContext';
import PalettePanel from './PalettePanel';
import { usePaletteDismiss } from './usePaletteDismiss';

/**
 * Collapsed-width, in px, of the admin layout's fixed left sidebar. The palette
 * clears it so its tab/panel sit in the gutter beside the canvas rather than on
 * top of the sidebar rail.
 */
const ADMIN_SIDEBAR_WIDTH = 64;

function palettePaperSx(isNarrow: boolean, leftOffset: number, zIndex: number) {
  if (isNarrow) {
    return {
      height: PALETTE_SHEET_HEIGHT,
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      boxShadow: 8,
      zIndex,
    };
  }
  return {
    left: leftOffset,
    top: PALETTE_HEADER_OFFSET,
    height: `calc(100% - ${PALETTE_HEADER_OFFSET}px)`,
    width: PALETTE_PANEL_WIDTH,
    boxShadow: 8,
    borderRight: '1px solid',
    borderColor: 'divider',
    zIndex,
  };
}

interface BlockPaletteProps {
  emptyCanvas?: boolean;
  fundOpportunityId: string;
  backendService: BackendService;
  /** Content model the canvas is editing. Library uploads stay on the parent fund. */
  modelType?: string;
  modelId?: string;
}

/**
 * Left-hand sidebar for the v2 editor (edit mode only) with two tabs: Blocks
 * (drag a block onto the page) and Media (drag a library video onto a slide).
 *
 * Rendered through a Portal as a fixed overlay so it never reduces the width the
 * content is rendered at, and so `position: fixed` resolves against the viewport
 * rather than a transformed ancestor in the editor tree.
 *
 * Click-toggled: an edge tab per tab (pill buttons on narrow viewports) opens
 * the panel; it closes on the × button, Escape, a click outside it, or after a
 * block is dragged out (a dragged video leaves it open, for dropping several).
 * No scrim — the canvas stays visible and usable. State lives in
 * EditorSidebarProvider so a slide can open the Media tab aimed at itself.
 *
 * While the canvas has no blocks (`emptyCanvas`) the sidebar opens on its own
 * (Blocks tab), so a new page shows the block choices without the user hunting
 * for the edge tab.
 */
export default function BlockPalette({
  emptyCanvas = false,
  fundOpportunityId,
  backendService,
  modelType = 'FundOpportunity',
  modelId,
}: BlockPaletteProps) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const { draggingType } = useBlockPaletteDrag();

  const { open, tab, openSidebar, openSidebarDocuments, closeSidebar, selectTab } =
    useEditorSidebar();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (emptyCanvas) openSidebar('blocks');
  }, [emptyCanvas, openSidebar]);

  useEffect(() => closeSidebar, [closeSidebar]);

  usePaletteDismiss({ open, close: closeSidebar, draggingType, panelRef });

  const overlayZIndex = theme.zIndex.drawer + 2;
  const leftOffset = isNarrow ? 0 : ADMIN_SIDEBAR_WIDTH;

  return (
    <Portal>
      {!open && (
        <BlockPaletteEdgeTabs
          isNarrow={isNarrow}
          leftOffset={leftOffset}
          zIndex={overlayZIndex}
          onOpenBlocks={() => openSidebar('blocks')}
          onOpenMedia={() => openSidebar('media')}
          onOpenDocuments={() => openSidebarDocuments()}
        />
      )}

      <Drawer
        variant="persistent"
        anchor={isNarrow ? 'bottom' : 'left'}
        open={open}
        sx={{
          zIndex: overlayZIndex,
          '& .MuiDrawer-paper': palettePaperSx(isNarrow, leftOffset, overlayZIndex),
        }}
      >
        <PalettePanel
          ref={panelRef}
          isNarrow={isNarrow}
          open={open}
          tab={tab}
          fundOpportunityId={fundOpportunityId}
          backendService={backendService}
          modelType={modelType}
          modelId={modelId ?? fundOpportunityId}
          onTabChange={selectTab}
          onClose={closeSidebar}
        />
      </Drawer>
    </Portal>
  );
}
